import Grid from 'apollo-react/components/Grid';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import LeftPanel from './LeftPanel';
import MiddlePanel from './MiddlePanel';
import RightPanel from './RightPanel';
import { setPanelLayoutConfig, setRightPanelMount } from '../../../actions/PanelLayoutActions';
import { PANEL_CONFIGURATIONS } from '../../../constants/PanelLayoutConstants';
import { DOCUMENT_VIEWER } from '../../../constants/Urls';
import { getTaskStatusDisabled } from '../../../containers/EntityDisplay/TaskStatus/selectors';
import { FORM_KEYS } from '../../../form-designer/constants/form-manager.constants';
import { getPanelLayout, isFormLoading } from '../../../form-designer/selectors/common.selectors';
import { getPreventFormMounting } from '../../../selectors/AppConfigsSelectors';
import { getIsArchiveMode } from '../../../selectors/AuthSelectors';
import { PanelContext } from '../../../utils/PanelConfig';

function DesktopPanelLayout({
  leftPanelChildren,
  leftPanelDisabled,
  middlePanelChildren,
  middlePanelFormFooter,
  rightPanelChildren,
  rightPanelDisabled,
  panelsProps = {},
  invertPanels,
}) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { config, configLocked, isRightPanelMounted } = useSelector(getPanelLayout);
  const preventFormMounting = useSelector(getPreventFormMounting);
  const hasStepForm = !useSelector(isFormLoading(FORM_KEYS.STEP));
  const hasDetailForm = !useSelector(isFormLoading(FORM_KEYS.DETAIL));
  const isTaskStatusPanelDisabled = useSelector(getTaskStatusDisabled);
  const isArchiveMode = useSelector(getIsArchiveMode);

  const setConfig = useCallback((config) => dispatch(setPanelLayoutConfig(config)), [dispatch]);
  const mountRightPanel = useCallback(() => dispatch(setRightPanelMount()), [dispatch]);

  const isMiddlePanelEnabled = hasStepForm || (hasDetailForm && pathname === DOCUMENT_VIEWER);

  const configProps = {
    config,
    setConfig,
  };

  useEffect(() => {
    if (pathname === DOCUMENT_VIEWER && preventFormMounting) {
      dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.ONLY_MIDDLE));
      return;
    }

    if (preventFormMounting && config !== PANEL_CONFIGURATIONS.RIGHT_COLLAPSED) {
      dispatch(setPanelLayoutConfig(PANEL_CONFIGURATIONS.RIGHT_COLLAPSED));
    }
  }, [dispatch, preventFormMounting, config, pathname]);

  return (
    <Grid style={{ height: '100%' }} container spacing={0}>
      <PanelContext.Provider value={{ layout: 'lf' }}>
        <LeftPanel
          {...configProps}
          hideButtons={isTaskStatusPanelDisabled || preventFormMounting}
          panelProps={panelsProps.leftPanel}
        >
          {leftPanelChildren}
        </LeftPanel>
      </PanelContext.Provider>
      {!invertPanels ? (
        <>
          <PanelContext.Provider value={{ layout: 'md' }}>
            <MiddlePanel
              {...configProps}
              hideButtons={configLocked}
              hideLeftButton={leftPanelDisabled}
              hideRightButton={rightPanelDisabled}
              middlePanelFormFooter={middlePanelFormFooter}
              panelProps={panelsProps.middlePanel}
              mountRightPanel={mountRightPanel}
              isRightPanelMounted={isRightPanelMounted}
              isMobileDevice={false}
            >
              {middlePanelChildren}
            </MiddlePanel>
          </PanelContext.Provider>
          <PanelContext.Provider value={{ layout: 'rt' }}>
            <RightPanel
              {...configProps}
              hideButtons={configLocked}
              isMiddlePanelEnabled={isMiddlePanelEnabled}
              isRightPanelMounted={isRightPanelMounted}
              isTaskStatusPanelEnabled={!isArchiveMode && !isTaskStatusPanelDisabled}
              panelProps={panelsProps.rightPanel}
            >
              {rightPanelChildren}
            </RightPanel>
          </PanelContext.Provider>
        </>
      ) : (
        <>
          <PanelContext.Provider value={{ layout: 'md' }}>
            <RightPanel
              {...configProps}
              hideButtons={configLocked}
              hideLeftButton={leftPanelDisabled}
              isMiddlePanelEnabled={isMiddlePanelEnabled}
              isRightPanelMounted={isRightPanelMounted}
              isTaskStatusPanelEnabled={!isArchiveMode && !isTaskStatusPanelDisabled}
              panelProps={panelsProps.rightPanel}
              invertedPanel
            >
              {rightPanelChildren}
            </RightPanel>
          </PanelContext.Provider>
          <PanelContext.Provider value={{ layout: 'rt' }}>
            <MiddlePanel
              {...configProps}
              hideButtons={configLocked}
              hideLeftButton={leftPanelDisabled}
              hideRightButton={rightPanelDisabled}
              middlePanelFormFooter={middlePanelFormFooter}
              panelProps={panelsProps.middlePanel}
              mountRightPanel={mountRightPanel}
              isRightPanelMounted={isRightPanelMounted}
              isMobileDevice={false}
              invertedPanel
            >
              {middlePanelChildren}
            </MiddlePanel>
          </PanelContext.Provider>
        </>
      )}
    </Grid>
  );
}

export default DesktopPanelLayout;
